<script setup lang="ts">
import type {
  PayloadTracking,
  PeriodDurationInquiry,
} from '~/types/inquiry/types'
import type { HouseDetails } from '~/types/house/types'

const props = defineProps<{
  house: HouseDetails
  payloadTracking: PayloadTracking
  periods: PeriodDurationInquiry[]
}>()
const emits = defineEmits<{
  'ask-a-question': [boolean]
}>()

const { logout } = useAuth()
const { user } = useUser()
const affixAnimate = useState('affixAnimate')
const { isSmallScreen, isMobile } = useBreakpoint()
const { isTreatment } = useExperiment('38576-house-question-v4')
const { booking, fetchBookings, setPrevStep } = useHouseInquiryBooking()
const { preOrder, setBookingSent } = useHousePreOrder()

const showMobileModal = ref(false)
const showMobileCalendar = ref(false)
const isBooking = ref(false)
const isModifyingDates = ref(false)

const handleShowMobileInquiry = (calendar: boolean) => {
  if (isTreatment.value) {
    emits('ask-a-question', true)
  } else {
    showMobileModal.value = true
  }
  isBooking.value = false
  showMobileCalendar.value = calendar
}

const handleCloseInquiry = () => {
  showMobileModal.value = false
  showMobileCalendar.value = false
  isModifyingDates.value = false
}

const handleShowMobileBooking = (calendar: boolean) => {
  isBooking.value = true
  showMobileModal.value = true
  showMobileCalendar.value = calendar
}

const handleShowMobileCalendar = () => {
  isBooking.value = false
  showMobileModal.value = true
  showMobileCalendar.value = true
  isModifyingDates.value = true
}

const handleHideCalendar = () => {
  // in mobile display, when calendar is hidden and house can be booked...
  if (
    showMobileModal.value &&
    !isBooking.value &&
    props.house.onlineReservation &&
    isModifyingDates.value
  ) {
    // close the modal and show the product page again
    handleCloseInquiry()
  } else {
    showMobileCalendar.value = false
  }
}

const displayModalCloseButton = computed(() =>
  Boolean(
    booking.value.currentStep !== 1 &&
      booking.value.startDate &&
      booking.value.endDate,
  ),
)
const bookingSent = computed(
  () => preOrder.value.bookingSent === props.house.id,
)
const typeformEmail = computed(() => user.value?.email || '')

onUnmounted(() => {
  if (bookingSent.value) setBookingSent()
})

if (booking.value.startDate && booking.value.endDate) {
  await fetchBookings(props.house.id)
}
</script>

<template>
  <template v-if="isSmallScreen">
    <BaseStickyBottom class="house-inquiry__sticky-bottom">
      <TheMobileInquiry
        :booking-sent="bookingSent"
        :house="house"
        @show-mobile-booking="handleShowMobileBooking"
        @show-mobile-calendar="handleShowMobileCalendar"
        @show-mobile-inquiry="handleShowMobileInquiry"
      />
    </BaseStickyBottom>

    <LazyBaseModalV2
      v-model="showMobileModal"
      :has-close-button="displayModalCloseButton"
      :has-back-button="booking.currentStep === 1"
      mobile-full-height
      size="xs"
      @close="handleCloseInquiry"
      @back="setPrevStep"
    >
      <template #content>
        <InquirySteps
          :booking-sent="bookingSent"
          :house="house"
          :logout="logout"
          :payload-tracking="payloadTracking"
          :periods="periods"
          :is-booking="isBooking"
          :show-calendar="showMobileCalendar"
          @hide-calendar="handleHideCalendar"
          @close-inquiry="handleCloseInquiry"
        />
      </template>
    </LazyBaseModalV2>
  </template>

  <BaseCol v-else class="pb-24" lg="10" xl="9">
    <div
      :class="[
        'sticky top-6 z-sticky -mt-72 min-h-[300px] border border-gray-200 bg-white p-6 duration-500 ease-in-out',
        { 'translate-y-[74px]': affixAnimate },
      ]"
    >
      <InquirySteps
        :booking-sent="bookingSent"
        :house="house"
        :logout="logout"
        :payload-tracking="payloadTracking"
        :periods="periods"
      />
    </div>
  </BaseCol>

  <InquiryFeedbackButtonHiddenTypeForm :email="typeformEmail" />

  <BaseFloatingButton
    v-if="!isMobile && isTreatment"
    :show-after-scroll="25"
    color="secondary"
    variant="outline"
    @btn-clicked="$emit('ask-a-question', true)"
  >
    <div class="flex items-center gap-2">
      <BaseIcon name="chatBubble" />
      <p class="m-0">{{ $t('global.askAQuestion') }}</p>
    </div>
  </BaseFloatingButton>
</template>

<style scoped>
@screen lg {
  :deep(.house-inquiry__sticky-bottom) {
    @apply hidden;
  }
}
</style>
